/**
 * Customer statement (spec §2.1, README M.1 "Send statement"): period presets
 * in Colombo time, the period label, the share text, and a reconciliation
 * check so the dialog can warn when the server's running balances do not add
 * up. Money stays in integer minor units.
 */
import { date, todayColombo } from './format';
import type { Statement } from '../../internals/repository/InvoiceRepository';

export type PeriodPreset = 'THIS_MONTH' | 'LAST_MONTH' | 'THIS_QUARTER' | 'THIS_YEAR' | 'CUSTOM';

export const PERIOD_PRESETS: { value: PeriodPreset; label: string }[] = [
  { value: 'THIS_MONTH', label: 'This month' },
  { value: 'LAST_MONTH', label: 'Last month' },
  { value: 'THIS_QUARTER', label: 'This quarter' },
  { value: 'THIS_YEAR', label: 'This year' },
  { value: 'CUSTOM', label: 'Custom' },
];

export interface Period { from: string; to: string }

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad(m)}-${pad(d)}`;
/** Last day of month m (1–12). */
const lastDay = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Preset → { from, to }. Current periods (this month, quarter, year) run to
 * today: a statement never reaches into the future. Custom returns the
 * given dates, or null while they are missing or reversed.
 */
export function periodFor(
  preset: PeriodPreset,
  today: string = todayColombo(),
  custom?: { from?: string; to?: string },
): Period | null {
  const [y, m] = today.split('-').map(Number);
  switch (preset) {
    case 'THIS_MONTH':
      return { from: iso(y, m, 1), to: today };
    case 'LAST_MONTH': {
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      return { from: iso(py, pm, 1), to: iso(py, pm, lastDay(py, pm)) };
    }
    case 'THIS_QUARTER':
      return { from: iso(y, Math.floor((m - 1) / 3) * 3 + 1, 1), to: today };
    case 'THIS_YEAR':
      return { from: iso(y, 1, 1), to: today };
    case 'CUSTOM': {
      const from = custom?.from ?? '';
      const to = custom?.to ?? '';
      if (!ISO_RE.test(from) || !ISO_RE.test(to) || from > to) return null;
      return { from, to };
    }
    default:
      return null;
  }
}

/** "1–31 Oct 2026", "1 Oct – 31 Dec 2026", "1 Dec 2025 – 31 Jan 2026", "5 Oct 2026". */
export function periodLabel(from?: string | null, to?: string | null): string {
  if (!from || !to) return date(from || to);
  if (from === to) return date(from);
  const [fy, fm, fd] = from.split('-').map(Number);
  const [ty, tm, td] = to.split('-').map(Number);
  if (fy !== ty) return `${date(from)} – ${date(to)}`;
  if (fm !== tm) return `${fd} ${MONTHS[fm - 1]} – ${td} ${MONTHS[tm - 1]} ${ty}`;
  return `${fd}–${td} ${MONTHS[tm - 1]} ${ty}`;
}

export const firstName = (name?: string | null) => (name || '').trim().split(/\s+/)[0] || '';

export function shareMessage(customerName: string | null | undefined, shopName: string | null | undefined, period: string, link: string): string {
  const who = firstName(customerName) || 'customer';
  const shop = (shopName || '').trim() || 'us';
  return `Dear ${who}, here is your statement from ${shop} for ${period}: ${link}`;
}

export const emailSubject = (shopName: string | null | undefined, period: string) =>
  `Statement from ${(shopName || '').trim() || 'your supplier'} for ${period}`;

export interface Reconciliation {
  ok: boolean;
  /** opening + Σdebit − Σcredit. */
  expectedClosingMinor: number;
  /** Index of the first entry whose running balance does not follow, or -1. */
  firstBadIndex: number;
}

/** opening + Σdebit − Σcredit must equal closing, and each running balance must follow from the one before. */
export function reconcile(s: Pick<Statement, 'openingBalanceMinor' | 'closingBalanceMinor' | 'entries'>): Reconciliation {
  let running = Math.round(Number(s.openingBalanceMinor) || 0);
  let firstBadIndex = -1;
  (s.entries || []).forEach((e, i) => {
    running += Math.round(Number(e.debitMinor) || 0) - Math.round(Number(e.creditMinor) || 0);
    if (firstBadIndex < 0 && running !== Math.round(Number(e.balanceMinor) || 0)) firstBadIndex = i;
  });
  const closingOk = running === Math.round(Number(s.closingBalanceMinor) || 0);
  return { ok: closingOk && firstBadIndex < 0, expectedClosingMinor: running, firstBadIndex };
}

export const ENTRY_LABEL: Record<string, string> = { INVOICE: 'Invoice', PAYMENT: 'Payment', CREDIT_NOTE: 'Credit note' };
